/**
 * Article Related Guides
 * 
 * Links to hub pages, collections, and tools related to the article.
 * Hub pages and collections use MoneyPageLink (hover preview on desktop).
 * Tools use plain links (tools are NOT money pages).
 * 
 * ALL links verified from Supabase — zero hardcoded URLs.
 * Always rendered (no quality dependency — Supabase-driven).
 */

import Link from 'next/link'
import { MoneyPageLink } from '@/components/ui/money-page-link'
import type { VerifiedHubPage } from '@/lib/blog-hub-linker'
import type { VerifiedTool } from '@/lib/blog-tool-linker'

interface RelatedGuide {
  title: string
  href: string
  type: 'hub' | 'collection' | 'tool'
  description?: string
  image?: string
}

interface ArticleRelatedGuidesProps {
  hubPages: VerifiedHubPage[]
  collections: { slug: string; displayName: string; url: string }[]
  tools: VerifiedTool[]
  hubImageMap?: Record<string, string>
  colImageMap?: Record<string, string>
}

export function ArticleRelatedGuides({ hubPages, collections, tools, hubImageMap, colImageMap }: ArticleRelatedGuidesProps) {
  // Build guides list from all verified sources
  const guides: RelatedGuide[] = []

  // Hub pages first (money pages — most valuable)
  for (const hub of hubPages.slice(0, 2)) {
    guides.push({
      title: hub.title,
      href: hub.url,
      type: 'hub',
      description: hub.description || undefined,
      image: hubImageMap?.[hub.slug],
    })
  }

  // Collections (money pages)
  for (const col of collections.slice(0, 2)) {
    guides.push({
      title: col.displayName,
      href: col.url,
      type: 'collection',
      image: colImageMap?.[col.slug],
    })
  }

  // Tools (NOT money pages — plain links)
  for (const tool of tools.slice(0, 2)) {
    guides.push({
      title: tool.name,
      href: tool.url,
      type: 'tool',
    })
  }

  if (guides.length === 0) return null

  return (
    <div className="mt-5 pt-2.5 border-t border-gray-100">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 flex items-center gap-1.5">
        <svg className="w-5 h-5 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        Related guides &amp; tools
      </h2>

      <div className="grid gap-2">
        {guides.map((guide, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group">
            {/* Icon based on type */}
            <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
              {guide.type === 'hub' && (
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              )}
              {guide.type === 'collection' && (
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                </svg>
              )}
              {guide.type === 'tool' && (
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              )}
            </span>

            {/* Link — MoneyPageLink for hub/collection, plain Link for tools */}
            <div className="flex-1 min-w-0">
              {(guide.type === 'hub' || guide.type === 'collection') ? (
                <MoneyPageLink
                  href={guide.href}
                  type={guide.type}
                  title={guide.title}
                  description={guide.description}
                  image={guide.image}
                >
                  {guide.title}
                </MoneyPageLink>
              ) : (
                <Link
                  href={guide.href}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {guide.title}
                </Link>
              )}
              {guide.type === 'hub' && <span className="block text-xs text-gray-500 mt-0.5">Buying Guide</span>}
              {guide.type === 'collection' && <span className="block text-xs text-gray-500 mt-0.5">Shop Collection</span>}
              {guide.type === 'tool' && <span className="block text-xs text-gray-500 mt-0.5">Free Calculator</span>}
            </div>

            <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  )
}
